import type {
  AntdTreeType,
  CategoryInterface,
  CharacteristicSlotType,
  CharacteristicValueType,
  NomenclatureGroupInterface,
} from '@/types'
import {
  getDisplayArticle,
  getDisplaySupplierArticles,
} from '@/utils/nomenclature/articles'

export interface NomenclatureRow {
  id: string
  code: number
  description: string
  article: string
  brandName: string
  categoryName: string
  unitName: string
  countryName: string
  parentName: string
  dynamicValue: string
  flashStatus: 'success' | 'error' | null
  errorMessage: string | null
}

export interface BackendSort {
  article: 'asc' | 'desc' | null
  description: 'asc' | 'desc' | null
}

export interface AttributeProcessingContextValue {
  // search bar
  query: string
  setQuery: (v: string) => void
  loading: boolean
  hasSearched: React.MutableRefObject<boolean>
  handleSearch: () => void
  showFilters: boolean
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>
  hasActiveFilters: boolean

  // filters
  barcode: string
  setBarcode: (v: string) => void
  brandId: string
  setBrandId: (v: string) => void
  brands: SimpleEntity[]
  brandsLoading: boolean
  loadBrands: () => Promise<void>
  categoryId: string
  setCategoryId: (v: string) => void
  categoriesLoading: boolean
  loadCategories: () => Promise<void>
  parentId: string
  setParentId: (v: string) => void
  parentsLoading: boolean
  loadParents: () => Promise<void>
  countryIds: string[]
  setCountryIds: (v: string[]) => void
  countries: SimpleEntity[]
  countriesLoading: boolean
  loadCountries: () => Promise<void>
  unitIds: string[]
  setUnitIds: (v: string[]) => void
  units: SimpleEntity[]
  unitsLoading: boolean
  loadUnits: () => Promise<void>
  isDiscontinued: boolean | null
  setIsDiscontinued: (v: boolean | null) => void
  isFilled: boolean | null
  setIsFilled: (v: boolean | null) => void
  multiplicity: boolean | null
  setMultiplicity: (v: boolean | null) => void
  emptyCharacteristicsFilter: EmptyCharacteristicsFilter
  setEmptyCharacteristicsFilter: (value: EmptyCharacteristicsFilter) => void
  activeChrcFilters: ActiveCharacteristicFilter[]
  addFilterOpen: boolean
  setAddFilterOpen: (v: boolean) => void
  characteristicsLoading: boolean
  characteristicsLoadedRef: React.MutableRefObject<boolean>
  availableCharacteristics: AvailableCharacteristic[]
  usedChrcCodes: Set<number>
  addCharacteristicFilter: (code: number) => Promise<void>
  removeCharacteristicFilter: (id: string) => void
  updateChrcFilter: (
    id: string,
    patch: Partial<ActiveCharacteristicFilter>
  ) => void
  resetAllFilters: () => void
  loadCharacteristicsTree: () => Promise<AvailableCharacteristic[]>
  refreshCharacteristicsTree: () => Promise<AvailableCharacteristic[]>
  refreshCategoriesTree: () => Promise<void>

  // tree selects
  globalCategoriesTree: CategoryInterface[]
  antdCategoriesTreeLeafOnly: AntdTreeType[]
  globalGroupsTree: NomenclatureGroupInterface[]
  antdParentsTreeLeafOnly: AntdTreeType[]

  // table
  rows: NomenclatureRow[]
  sortedRows: NomenclatureRow[]
  backendSort: BackendSort
  handleBackendSort: (col: keyof BackendSort) => void
  dynamicSortDir: 'asc' | 'desc' | null
  handleDynamicSort: () => void
  totalRows: number
  loadingMore: boolean
  loadMoreRef: (node?: Element | null) => void
  selectedIds: Set<string>
  selectedPreviewRows: NomenclatureRow[]
  setSelectedIds: React.Dispatch<React.SetStateAction<Set<string>>>
  allPageSelected: boolean
  somePageSelected: boolean
  handleSelectAll: () => void
  handleRowClick: (id: string, index: number, e: React.MouseEvent) => void
  toggleOne: (id: string) => void
  removeRow: (id: string) => void
  computeDynamicCellValue: (row: NomenclatureRow) => string
  dynamicColumnLabel: string
  setDynamicColumnLabel: React.Dispatch<React.SetStateAction<string>>
  setSelectedDetails: React.Dispatch<
    React.SetStateAction<NomenclatureDetailsItem[]>
  >

  // right panel / tabs
  activeTab: ActiveTab
  handleTabChange: (tab: string) => void
  isRightPanelDisabled: boolean
  detailsLoading: boolean
  selectedDetails: NomenclatureDetailsItem[]
  isPanelLoading: boolean

  // processing
  selectedMainField: string | null
  handleMainFieldSelect: (key: string) => void
  selectedChrcCode: number | null
  intersectedCharacteristics: Characteristic[]
  handleChrcSelect: (code: number, name: string) => Promise<void>
  action: MassUpdateAction
  setAction: React.Dispatch<React.SetStateAction<MassUpdateAction>>
  processValue: string
  setProcessValue: React.Dispatch<React.SetStateAction<string>>
  processValues: string[]
  setProcessValues: React.Dispatch<React.SetStateAction<string[]>>
  isBarcodeInvalid: boolean
  processValuesError: string | null
  isListFieldSelected: boolean
  supplierArticleOptions: string[]
  /** Значения присоединений для SLOTTED_REFERENCE: индекс массива = слот - 1. */
  processSlotValues: string[]
  setProcessSlotValues: React.Dispatch<React.SetStateAction<string[]>>
  /** Обновляет присоединение по индексу (очистка/динамическое добавление поля). */
  updateSlotValue: (index: number, value: string) => void
  /** Разрешённый лимит присоединений для выбранной характеристики. */
  processSlotsCount: number
  /** У выбранных товаров разный `slotsCount` — часть сохранится, часть упадёт. */
  processSlotsMismatch: boolean
  /** Ни у одного выбранного товара категория не задала лимит — поля рендерятся динамически. */
  processSlotsDynamic: boolean
  /** Выбранная характеристика — слотовая («Диаметр присоединения»). */
  isSlottedChrcSelected: boolean
  /** Номер первого пропуска между заполненными присоединениями, 0 — пропусков нет. */
  slottedGapIndex: number
  currentMainField: (typeof MAIN_FIELDS)[0] | undefined
  isClearDisabled: boolean
  isSetDisabled: boolean
  isProcessDisabled: boolean
  processing: boolean
  handleProcess: () => Promise<void>
  refFieldLoading: boolean
  dictionaryValues: DictionaryValue[]
  dictionaryLoading: boolean

  // images
  imageReady: boolean
  setImageReady: (v: boolean) => void
  imageVersion: number

  // documents/attachments
  attachDocs: AttachFile[]
  attachDocsLoading: boolean
  uploading: boolean
  dragOver: boolean
  setDragOver: (v: boolean) => void
  pendingFiles: PendingFile[]
  mainPendingImageId: string | null
  setMainPendingImageId: (id: string) => void
  addPendingFiles: (
    files: FileList | File[] | null,
    attachType?: AttachmentType
  ) => void
  removePendingFile: (id: string) => void
  uploadPendingFiles: (scope: 'IMAGES' | 'DOCUMENTS') => Promise<void>
  downloadAttachFile: (file: AttachFile) => void
  deleteAttachFile: (
    fileName: string,
    fileType: AttachmentType
  ) => Promise<void>
  updateAttachFileType: (
    fileName: string,
    fileExtension: string,
    newType: AttachmentType
  ) => Promise<void>
}

export interface NomenclatureExtendedModel {
  id: string
  code: number
  description: string
  article: string
  articleOriginal?: string
  supplierArticles?: string[]
  supplierArticlesOriginal?: string[]
  brandDto?: { id: string; description: string }
  categoryDto?: { id: string; description: string }
  unitOfMeasurementDto?: { id: string; description: string }
  countryDto?: { id: string; description: string }
  parentModel?: { id: string; description: string }
  mainImage?: string
  itemLength: number
  itemWidth: number
  itemHeight: number
  itemWeight: number
  warranty: number
  comment: string
  details?: string
  barcode: string
  isFilled: boolean
  isDiscontinued: boolean
  isGroup: boolean
  multiplicity?: boolean
}

export interface Characteristic {
  nomenclatureId: string
  chrcCode: number
  chrcId?: string
  chrcName: string
  chrcValue?: string
  /**
   * UUID значения из справочника для `REFERENCE` (записка: бэкенд отдаёт его
   * отдельным полем, т.к. в `chrcValue` лежит текст для отображения).
   * Для `SLOTTED_REFERENCE` — `null`, UUID'ы лежат в `slots[].valueId`.
   */
  chrcValueId?: string | null
  valueType: CharacteristicValueType
  /**
   * Лимит присоединений категории для `SLOTTED_REFERENCE` — единственный
   * источник количества полей ввода (записка §1).
   */
  slotsCount?: number | null
  /**
   * Только фактически заполненные присоединения, разреженный массив.
   * Сопоставляется с полями по `index`, а не по позиции.
   */
  slots?: CharacteristicSlotType[]
}

export interface NomenclatureDetailsItem {
  id: string
  code: number
  nomenclatureExtendedModel: NomenclatureExtendedModel
  characteristics: Characteristic[]
}

export interface MainFieldConfig {
  key: string
  label: string
  type:
    | 'STRING'
    | 'NUMERIC'
    | 'BOOLEAN'
    | 'STRING_LIST'
    | 'REFERENCE_BRAND'
    | 'REFERENCE_CATEGORY'
    | 'REFERENCE_UNIT'
    | 'REFERENCE_COUNTRY'
    | 'REFERENCE_PARENT'
  clearable: boolean
  readonly?: boolean
}

export interface SimpleEntity {
  id: string
  description: string
}

export interface DictionaryValue {
  id: string
  value: string
}

export type ActiveTab =
  | 'ADDITIONAL'
  | 'MAIN'
  | 'ATTACHMENTS'
  | 'CREATE'
  | 'CATEGORIES'

export interface AvailableCharacteristic {
  id: string
  code: number
  name: string
  /** Приходит из `CharacteristicDto.valueType`, включая `SLOTTED_REFERENCE`. */
  type: CharacteristicValueType
  options: string[]
  min?: number
  max?: number
  unit?: string
  unitId?: string
  status?: 'DRAFT' | 'APPROVED' | 'REJECTED'
}

export interface ReferenceOption {
  id: string
  value: string
}

export interface ActiveCharacteristicFilter {
  id: string
  code: number
  name: string
  type: CharacteristicValueType
  unit?: string
  options: string[]
  referenceOptions?: ReferenceOption[]
  optionsLoading?: boolean
  /**
   * Для `SLOTTED_REFERENCE` — значения в формате `"<присоединение>:<значение>"`
   * (записка §3), например `["1:32", "1:40", "2:25"]`.
   */
  chosenOptions: string[]
  rangeMin: string
  rangeMax: string
  /**
   * Сколько рядов присоединений показано в фильтре. `slotsCount` — лимит
   * категории, а фильтр в редакторе не привязан к категории, поэтому ряды
   * добавляет сам пользователь. По записке §3 слоты можно отправлять не все:
   * не вошедшие в запрос фильтруются как «не важно».
   */
  slotRows?: number
  matchUniform?: boolean
}

export type AttachmentType =
  | 'IMAGE'
  | 'PASSPORT'
  | 'SCHEME'
  | 'PROMO_MATERIAL'
  | 'CERTIFICATE'

export interface AttachFile {
  name?: string
  extension: string
  ContentType: string
  dvdan: string
  type: AttachmentType
}

export interface PendingFile {
  id: string
  file: File
  type: AttachmentType
  previewUrl?: string
}

export const MAIN_FIELDS: MainFieldConfig[] = [
  {
    key: 'CODE',
    label: 'Код',
    type: 'NUMERIC',
    clearable: false,
    readonly: true,
  },
  {
    key: 'DESCRIPTION',
    label: 'Наименование',
    type: 'STRING',
    clearable: true,
  },
  {
    key: 'ARTICLE',
    label: 'Артикул (производителя)',
    type: 'STRING',
    clearable: true,
  },
  {
    key: 'SUPPLIER_ARTICLES',
    label: 'Артикулы (поставщика)',
    type: 'STRING_LIST',
    clearable: true,
  },
  { key: 'BARCODE', label: 'Штрихкод', type: 'STRING', clearable: true },
  { key: 'COMMENT', label: 'Комментарий', type: 'STRING', clearable: true },
  { key: 'DETAILS', label: 'Описание', type: 'STRING', clearable: true },
  { key: 'BRAND', label: 'Бренд', type: 'REFERENCE_BRAND', clearable: false },
  {
    key: 'CATEGORY',
    label: 'Категория',
    type: 'REFERENCE_CATEGORY',
    clearable: false,
  },
  {
    key: 'UNIT_OF_MEASUREMENT',
    label: 'Единица измерения',
    type: 'REFERENCE_UNIT',
    clearable: false,
  },
  {
    key: 'COUNTRY_OF_ORIGIN',
    label: 'Страна происхождения',
    type: 'REFERENCE_COUNTRY',
    clearable: false,
  },
  {
    key: 'PARENT_ID',
    label: 'Группа номенклатуры',
    type: 'REFERENCE_PARENT',
    clearable: false,
  },
  { key: 'ITEM_LENGTH', label: 'Длина (мм)', type: 'NUMERIC', clearable: true },
  { key: 'ITEM_WIDTH', label: 'Ширина (мм)', type: 'NUMERIC', clearable: true },
  {
    key: 'ITEM_HEIGHT',
    label: 'Высота (мм)',
    type: 'NUMERIC',
    clearable: true,
  },
  { key: 'ITEM_WEIGHT', label: 'Вес (кг)', type: 'NUMERIC', clearable: true },
  {
    key: 'WARRANTY',
    label: 'Гарантия (мес)',
    type: 'NUMERIC',
    clearable: true,
  },
  {
    key: 'IS_DISCONTINUED',
    label: 'Снят с производства',
    type: 'BOOLEAN',
    clearable: false,
  },
  { key: 'IS_FILLED', label: 'Заполнен', type: 'BOOLEAN', clearable: false },
  {
    key: 'MULTIPLICITY',
    label: 'Кратность',
    type: 'BOOLEAN',
    clearable: false,
  },
  { key: 'ID', label: 'ID', type: 'STRING', clearable: false, readonly: true },
  {
    key: 'IS_GROUP',
    label: 'Является группой',
    type: 'BOOLEAN',
    clearable: false,
    readonly: true,
  },
  {
    key: 'MAIN_IMAGE',
    label: 'Главное фото',
    type: 'STRING',
    clearable: false,
    readonly: true,
  },
  {
    key: 'SERVICE_CENTER',
    label: 'Сервисный центр',
    type: 'STRING',
    clearable: false,
    readonly: true,
  },
  {
    key: 'IMPORTER',
    label: 'Импортёр',
    type: 'STRING',
    clearable: false,
    readonly: true,
  },
  {
    key: 'ORDER_QUANTITY',
    label: 'Количество в заказе',
    type: 'NUMERIC',
    clearable: false,
    readonly: true,
  },
]

export function getMainFieldDisplayValue(
  model: NomenclatureExtendedModel,
  fieldKey: string
): string {
  switch (fieldKey) {
    case 'DESCRIPTION':
      return model.description ?? ''
    case 'ARTICLE':
      return getDisplayArticle(model)
    case 'SUPPLIER_ARTICLES':
      return getDisplaySupplierArticles(model).join(', ')
    case 'BARCODE':
      return model.barcode ?? ''
    case 'COMMENT':
      return model.comment ?? ''
    case 'DETAILS':
      return model.details ?? ''
    case 'BRAND':
      return model.brandDto?.description ?? ''
    case 'CATEGORY':
      return model.categoryDto?.description ?? ''
    case 'UNIT_OF_MEASUREMENT':
      return model.unitOfMeasurementDto?.description ?? ''
    case 'COUNTRY_OF_ORIGIN':
      return model.countryDto?.description ?? ''
    case 'PARENT_ID':
      return model.parentModel?.description ?? ''
    case 'ITEM_LENGTH':
      return String(model.itemLength ?? 0)
    case 'ITEM_WIDTH':
      return String(model.itemWidth ?? 0)
    case 'ITEM_HEIGHT':
      return String(model.itemHeight ?? 0)
    case 'ITEM_WEIGHT':
      return String(model.itemWeight ?? 0)
    case 'WARRANTY':
      return String(model.warranty ?? 0)
    case 'IS_DISCONTINUED':
      return model.isDiscontinued ? 'Да' : 'Нет'
    case 'IS_FILLED':
      return model.isFilled ? 'Да' : 'Нет'
    case 'MULTIPLICITY':
      return model.multiplicity != null
        ? model.multiplicity
          ? 'Да'
          : 'Нет'
        : ''
    case 'ID':
      return model.id ?? ''
    case 'CODE':
      return String(model.code ?? '')
    case 'IS_GROUP':
      return model.isGroup ? 'Да' : 'Нет'
    case 'MAIN_IMAGE':
      return model.mainImage ? 'Есть' : 'Нет'
    default:
      return ''
  }
}

export function getMainFieldIdValue(
  model: NomenclatureExtendedModel,
  fieldKey: string
): string {
  switch (fieldKey) {
    case 'BRAND':
      return model.brandDto?.id ?? ''
    case 'CATEGORY':
      return model.categoryDto?.id ?? ''
    case 'UNIT_OF_MEASUREMENT':
      return model.unitOfMeasurementDto?.id ?? ''
    case 'COUNTRY_OF_ORIGIN':
      return model.countryDto?.id ?? ''
    case 'PARENT_ID':
      return model.parentModel?.id ?? ''
    default:
      return getMainFieldDisplayValue(model, fieldKey)
  }
}

export function getMainFieldListValues(
  model: NomenclatureExtendedModel,
  fieldKey: string
): string[] {
  return fieldKey === 'SUPPLIER_ARTICLES'
    ? getDisplaySupplierArticles(model)
    : []
}

export interface CharacteristicListItemDto {
  id: string
  code: number
  description: string
  valueType: CharacteristicValueType
  status?: AvailableCharacteristic['status']
  unitOfMeasurementDto?: { id?: string; description?: string } | null
}

export interface CharacteristicListResponse {
  characteristics?: CharacteristicListItemDto[]
}

export interface CharacteristicValuesResponse {
  items?: DictionaryValue[]
}

export type NomenclatureListItem = Pick<
  NomenclatureExtendedModel,
  | 'id'
  | 'code'
  | 'description'
  | 'article'
  | 'brandDto'
  | 'categoryDto'
  | 'unitOfMeasurementDto'
  | 'countryDto'
  | 'parentModel'
>

export interface EmptyCharacteristicsFilter {
  hasEmptyCharacteristics: boolean | null
  codes: number[]
  slots: Record<string, number[]>
}

export interface NomenclatureAdminSearchResponse {
  nomenclatures: {
    content: NomenclatureListItem[]
    totalElements: number
  }
}

export type BackendSortOrder = 'ASC' | 'DESC'

export interface NomenclatureAdminSearchRequest {
  page: number
  pageSize: number
  brandIds?: string[]
  categoryIds?: string[]
  parentIds?: string[]
  countryOfOriginIds?: string[]
  unitOfMeasurementIds?: string[]
  filter?: Record<string, string[]>
  ranges?: Record<string, { min?: number; max?: number }>
  query?: string
  barcode?: string
  isDiscontinued?: boolean
  isFilled?: boolean
  multiplicity?: boolean
  sortByArticle?: string
  sortByDescription?: string
  matchUniformSlottedCharacteristics?: true
  hasEmptyCharacteristics?: boolean
  emptyCharacteristicCodes?: number[]
  emptySlots?: Record<string, number[]>
}

export interface BatchNomenclatureDetailsItem {
  nomenclatureExtendedModel?: NomenclatureExtendedModel
  characteristics?: Characteristic[]
  details?: {
    nomenclatureExtendedModel?: NomenclatureExtendedModel
    details?: string
    characteristics?: Characteristic[]
  }
}

export interface BatchNomenclatureDetailsResponse {
  items?: BatchNomenclatureDetailsItem[]
}

export type MassUpdateAction = 'SET' | 'ADD' | 'CLEAR' | 'REMOVE'

export interface MassUpdateRequest {
  nomenclatureIds: string[]
  action: MassUpdateAction
  value?: string | null
  values?: string[]
  fieldType?: 'MAIN' | 'ADDITIONAL'
  mainField?: string | null
  chrcCode?: number | null
}
